/**
 * Sector operations — Hospital Patient Directory client action definitions.
 * R39 C8.a (plan docs/plans/sector-operations.md §2 SO-D18, SO-D20, SO-D34, SO-P15, SO-P255).
 */

import type { JSONSchemaType } from 'ajv'
import type { ActionContext, ActionDefinition, ActionOutcome, ActionRisk } from '../action'
import { isWorkspaceRole, roleCanPerform } from '../../../utils/workspacePermissions'
import {
  getHosPatientSettings,
  setHosPatientDirectoryEnabled,
  setHosPatientNotice,
  createHosPatient,
  updateHosPatient,
  recordHosConsent,
  withdrawHosConsent,
  checkInHosVisit,
  checkOutHosVisit,
  listHosPatients,
  getHosPatient,
  type CreateHosPatientInput,
  type UpdateHosPatientInput,
  type CheckInHosVisitInput,
  type CheckOutHosVisitInput,
  type RecordHosConsentInput,
  type WithdrawHosConsentInput,
  type SetHosPatientDirectoryEnabledInput,
  type SetHosPatientNoticeInput,
  type ListHosPatientsInput,
  type GetHosPatientInput,
  type GetHosPatientSettingsInput,
} from '../../hosPatientsService'
import { setHosPatientLegalSetup } from '../../ownerConsoleService'

const opsAdminPermission = (context: { actor: { role?: unknown } }): boolean =>
  isWorkspaceRole(context.actor.role) ? roleCanPerform(context.actor.role, 'ADMIN') : false

const opsMemberOrHelperPermission = (context: { actor: { role?: unknown } }): boolean => {
  if (context.actor.role === 'HELPER') return true
  return isWorkspaceRole(context.actor.role)
    ? roleCanPerform(context.actor.role, 'VIEWER')
    : false
}

const formatOpsError = (err: unknown): string =>
  err instanceof Error ? err.message : String(err)

function buildHosPatientAction<Args, Result>(opts: {
  id: string
  label: string
  description: string
  risk: ActionRisk
  argsSchema: JSONSchemaType<Args>
  permission: (context: ActionContext) => boolean
  handler: (args: Args, workspaceId: string) => Promise<Result>
}): ActionDefinition<Args, Result> {
  return {
    id: opts.id,
    label: opts.label,
    description: opts.description,
    risk: opts.risk,
    domain: 'ops',
    surfaces: ['catalog'],
    argsSchema: opts.argsSchema,
    permission: opts.permission,
    run: async (args, context): Promise<ActionOutcome<Result>> => {
      const correlationId = context.correlationId ?? 'ops-correlation'
      if (!context.workspaceId) {
        const message = `Workspace ID is required for ${opts.id}`
        context.showToast?.(message, 'error')
        return { kind: 'FAILED', message, recoverable: false, correlationId }
      }
      try {
        const res = await opts.handler(args, context.workspaceId)
        return {
          kind: 'COMPLETED',
          message: `Completed ${opts.label}.`,
          result: res,
          correlationId,
        }
      } catch (err) {
        const message = formatOpsError(err)
        context.showToast?.(message, 'error')
        return {
          kind: 'FAILED',
          message,
          recoverable: true,
          correlationId,
        }
      }
    },
  }
}

// Action IDs
export const OPS_GET_HOS_PATIENT_SETTINGS_ACTION_ID = 'ops.getHosPatientSettings'
export const OPS_SET_HOS_PATIENT_DIRECTORY_ENABLED_ACTION_ID = 'ops.setHosPatientDirectoryEnabled'
export const OPS_SET_HOS_PATIENT_NOTICE_ACTION_ID = 'ops.setHosPatientNotice'
export const OPS_CREATE_HOS_PATIENT_ACTION_ID = 'ops.createHosPatient'
export const OPS_UPDATE_HOS_PATIENT_ACTION_ID = 'ops.updateHosPatient'
export const OPS_RECORD_HOS_CONSENT_ACTION_ID = 'ops.recordHosConsent'
export const OPS_WITHDRAW_HOS_CONSENT_ACTION_ID = 'ops.withdrawHosConsent'
export const OPS_CHECK_IN_HOS_VISIT_ACTION_ID = 'ops.checkInHosVisit'
export const OPS_CHECK_OUT_HOS_VISIT_ACTION_ID = 'ops.checkOutHosVisit'
export const OPS_LIST_HOS_PATIENTS_ACTION_ID = 'ops.listHosPatients'
export const OPS_GET_HOS_PATIENT_ACTION_ID = 'ops.getHosPatient'
export const OPS_SET_HOS_PATIENT_LEGAL_SETUP_ACTION_ID = 'ops.setHosPatientLegalSetup'

// Args Schemas
const emptyArgsSchema: JSONSchemaType<Record<string, never>> = {
  type: 'object',
  properties: {},
  required: [],
  additionalProperties: false,
}

const setLegalSetupArgsSchema: JSONSchemaType<{ confirmed: boolean }> = {
  type: 'object',
  properties: {
    confirmed: { type: 'boolean' },
  },
  required: ['confirmed'],
  additionalProperties: false,
}

const setEnabledArgsSchema: JSONSchemaType<{ enabled: boolean }> = {
  type: 'object',
  properties: {
    enabled: { type: 'boolean' },
  },
  required: ['enabled'],
  additionalProperties: false,
}

const setNoticeArgsSchema: JSONSchemaType<{ noticeText: string }> = {
  type: 'object',
  properties: {
    noticeText: { type: 'string', minLength: 1, maxLength: 4000 },
  },
  required: ['noticeText'],
  additionalProperties: false,
}

const createPatientArgsSchema: JSONSchemaType<{
  name: string
  phone?: string
  hospitalRef?: string
  isMinor?: boolean
  representative?: { name: string; phone?: string; relation?: string }
  consentBasis: 'CONSENT' | 'LEGITIMATE_USE'
}> = {
  type: 'object',
  properties: {
    name: { type: 'string', minLength: 1, maxLength: 120 },
    phone: { type: 'string', maxLength: 30, nullable: true },
    hospitalRef: { type: 'string', maxLength: 60, nullable: true },
    isMinor: { type: 'boolean', nullable: true },
    representative: {
      type: 'object',
      properties: {
        name: { type: 'string', minLength: 1, maxLength: 120 },
        phone: { type: 'string', maxLength: 30, nullable: true },
        relation: { type: 'string', maxLength: 60, nullable: true },
      },
      required: ['name'],
      nullable: true,
      additionalProperties: false,
    },
    consentBasis: { type: 'string', enum: ['CONSENT', 'LEGITIMATE_USE'] },
  },
  required: ['name', 'consentBasis'],
  additionalProperties: false,
}

const updatePatientArgsSchema: JSONSchemaType<{
  patientId: string
  name?: string
  phone?: string
  hospitalRef?: string
  isMinor?: boolean
  representative?: { name: string; phone?: string; relation?: string }
}> = {
  type: 'object',
  properties: {
    patientId: { type: 'string', minLength: 1 },
    name: { type: 'string', minLength: 1, maxLength: 120, nullable: true },
    phone: { type: 'string', maxLength: 30, nullable: true },
    hospitalRef: { type: 'string', maxLength: 60, nullable: true },
    isMinor: { type: 'boolean', nullable: true },
    representative: {
      type: 'object',
      properties: {
        name: { type: 'string', minLength: 1, maxLength: 120 },
        phone: { type: 'string', maxLength: 30, nullable: true },
        relation: { type: 'string', maxLength: 60, nullable: true },
      },
      required: ['name'],
      nullable: true,
      additionalProperties: false,
    },
  },
  required: ['patientId'],
  additionalProperties: false,
}

const recordConsentArgsSchema: JSONSchemaType<{
  patientId: string
  basis: 'CONSENT' | 'LEGITIMATE_USE'
}> = {
  type: 'object',
  properties: {
    patientId: { type: 'string', minLength: 1 },
    basis: { type: 'string', enum: ['CONSENT', 'LEGITIMATE_USE'] },
  },
  required: ['patientId', 'basis'],
  additionalProperties: false,
}

const withdrawConsentArgsSchema: JSONSchemaType<{
  patientId: string
}> = {
  type: 'object',
  properties: {
    patientId: { type: 'string', minLength: 1 },
  },
  required: ['patientId'],
  additionalProperties: false,
}

const checkInVisitArgsSchema: JSONSchemaType<{
  patientId: string
  department: string
  arrivedAt?: string
}> = {
  type: 'object',
  properties: {
    patientId: { type: 'string', minLength: 1 },
    department: { type: 'string', minLength: 1 },
    arrivedAt: { type: 'string', nullable: true },
  },
  required: ['patientId', 'department'],
  additionalProperties: false,
}

const checkOutVisitArgsSchema: JSONSchemaType<{
  visitId: string
  leftAt?: string
}> = {
  type: 'object',
  properties: {
    visitId: { type: 'string', minLength: 1 },
    leftAt: { type: 'string', nullable: true },
  },
  required: ['visitId'],
  additionalProperties: false,
}

const listPatientsArgsSchema: JSONSchemaType<{
  search?: string
  limit?: number
}> = {
  type: 'object',
  properties: {
    search: { type: 'string', nullable: true },
    limit: { type: 'number', nullable: true },
  },
  required: [],
  additionalProperties: false,
}

const getPatientArgsSchema: JSONSchemaType<{
  patientId: string
}> = {
  type: 'object',
  properties: {
    patientId: { type: 'string', minLength: 1 },
  },
  required: ['patientId'],
  additionalProperties: false,
}

export const HOS_PATIENT_DOMAIN_ACTIONS = {
  getHosPatientSettings: buildHosPatientAction({
    id: OPS_GET_HOS_PATIENT_SETTINGS_ACTION_ID,
    label: 'Get patient directory settings',
    description: 'Retrieves patient directory configuration and legal setup status.',
    risk: 'LOW',
    argsSchema: emptyArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (_args, workspaceId) =>
      getHosPatientSettings({ workspaceId } as GetHosPatientSettingsInput),
  }),
  setHosPatientDirectoryEnabled: buildHosPatientAction({
    id: OPS_SET_HOS_PATIENT_DIRECTORY_ENABLED_ACTION_ID,
    label: 'Set patient directory enabled',
    description: 'Enables or disables the light patient directory.',
    risk: 'HIGH',
    argsSchema: setEnabledArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setHosPatientDirectoryEnabled({ workspaceId, ...args } as SetHosPatientDirectoryEnabledInput),
  }),
  setHosPatientNotice: buildHosPatientAction({
    id: OPS_SET_HOS_PATIENT_NOTICE_ACTION_ID,
    label: 'Set patient directory notice',
    description: 'Updates patient directory operational notice and bumps version.',
    risk: 'HIGH',
    argsSchema: setNoticeArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setHosPatientNotice({ workspaceId, ...args } as SetHosPatientNoticeInput),
  }),
  createHosPatient: buildHosPatientAction({
    id: OPS_CREATE_HOS_PATIENT_ACTION_ID,
    label: 'Create patient record',
    description: 'Creates a light patient record with pseudonymous reference and consent.',
    risk: 'MEDIUM',
    argsSchema: createPatientArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      createHosPatient({ workspaceId, ...args } as CreateHosPatientInput),
  }),
  updateHosPatient: buildHosPatientAction({
    id: OPS_UPDATE_HOS_PATIENT_ACTION_ID,
    label: 'Update patient record',
    description: 'Updates basic non-clinical details on a patient record.',
    risk: 'MEDIUM',
    argsSchema: updatePatientArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      updateHosPatient({ workspaceId, ...args } as UpdateHosPatientInput),
  }),
  recordHosConsent: buildHosPatientAction({
    id: OPS_RECORD_HOS_CONSENT_ACTION_ID,
    label: 'Record patient consent',
    description: 'Records patient operational notice consent or legitimate use basis.',
    risk: 'MEDIUM',
    argsSchema: recordConsentArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      recordHosConsent({ workspaceId, ...args } as RecordHosConsentInput),
  }),
  withdrawHosConsent: buildHosPatientAction({
    id: OPS_WITHDRAW_HOS_CONSENT_ACTION_ID,
    label: 'Withdraw patient consent',
    description: 'Withdraws consent, clears patient name and phone, preserves pseudoRef.',
    risk: 'HIGH',
    argsSchema: withdrawConsentArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      withdrawHosConsent({ workspaceId, ...args } as WithdrawHosConsentInput),
  }),
  checkInHosVisit: buildHosPatientAction({
    id: OPS_CHECK_IN_HOS_VISIT_ACTION_ID,
    label: 'Check in patient visit',
    description: 'Records patient arrival and department check-in.',
    risk: 'LOW',
    argsSchema: checkInVisitArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      checkInHosVisit({ workspaceId, ...args } as CheckInHosVisitInput),
  }),
  checkOutHosVisit: buildHosPatientAction({
    id: OPS_CHECK_OUT_HOS_VISIT_ACTION_ID,
    label: 'Check out patient visit',
    description: 'Records patient departure from hospital department.',
    risk: 'LOW',
    argsSchema: checkOutVisitArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      checkOutHosVisit({ workspaceId, ...args } as CheckOutHosVisitInput),
  }),
  listHosPatients: buildHosPatientAction({
    id: OPS_LIST_HOS_PATIENTS_ACTION_ID,
    label: 'List patients',
    description: 'Lists patients matching search prefix or pseudoRef.',
    risk: 'LOW',
    argsSchema: listPatientsArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      listHosPatients({ workspaceId, ...args } as ListHosPatientsInput),
  }),
  getHosPatient: buildHosPatientAction({
    id: OPS_GET_HOS_PATIENT_ACTION_ID,
    label: 'Get patient details',
    description: 'Retrieves patient details, visits, and consent history.',
    risk: 'LOW',
    argsSchema: getPatientArgsSchema,
    permission: opsMemberOrHelperPermission,
    handler: (args, workspaceId) =>
      getHosPatient({ workspaceId, ...args } as GetHosPatientInput),
  }),
  setHosPatientLegalSetup: buildHosPatientAction({
    id: OPS_SET_HOS_PATIENT_LEGAL_SETUP_ACTION_ID,
    label: 'Set hospital patient legal setup',
    description: 'Confirms or revokes founder legal setup for hospital patient directory.',
    risk: 'HIGH',
    argsSchema: setLegalSetupArgsSchema,
    permission: opsAdminPermission,
    handler: (args, workspaceId) =>
      setHosPatientLegalSetup({ workspaceId, confirmed: args.confirmed }),
  }),
}
